import { FileTextIcon, UploadIcon } from 'lucide-react';
import { type DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/ui/card';
import { Input } from '../../../shared/ui/input';
import { type ScriptRefusal, useUploadScript } from './model';

const REFUSAL_KEYS = {
  not_pdf: 'script.upload.notPdf',
  too_large: 'script.upload.tooLarge',
  unpriceable: 'script.upload.unpriceable',
} as const satisfies Record<ScriptRefusal, string>;

/**
 * Step one of a script order: a drop zone for the PDF. The upload starts as soon as a file is chosen or dropped,
 * and the customer waits on "Reading your script…" until the form can open. A script that cannot be priced is
 * said so, with the upload offered again; a failure of ours offers the same file again.
 */
export function UploadScript({ projectId }: { projectId: string }) {
  const { t } = useTranslation('order');
  const { state, upload, retry, busy } = useUploadScript(projectId);
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function choose(file: File | undefined) {
    if (file && !busy) void upload(file);
    if (input.current) input.current.value = '';
  }
  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setOver(false);
    choose(event.dataTransfer.files[0]);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="font-sans">
          <h2>{t('script.upload.title')}</h2>
        </CardTitle>
        <CardDescription>{t('script.upload.help')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {busy ? (
          <div
            role="status"
            className="grid justify-items-center gap-2 rounded-sm border border-dashed border-input px-4 py-10 text-center"
          >
            <FileTextIcon aria-hidden="true" className="size-8 animate-pulse text-primary" />
            <p className="font-bold text-heading">
              {state.phase === 'uploading'
                ? t('script.upload.uploading')
                : t('script.upload.reading')}
            </p>
            <p className="text-sm text-muted-foreground">{t('script.upload.readingHelp')}</p>
          </div>
        ) : (
          // biome-ignore lint/a11y/noStaticElementInteractions: a drop target; the file input inside it is the control
          <div
            data-over={over}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            className={cn(
              'grid justify-items-center gap-3 rounded-sm border border-dashed border-input px-4 py-8 text-center transition-colors duration-150',
              over && 'border-primary bg-accent',
            )}
          >
            <UploadIcon aria-hidden="true" className="size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {over ? t('script.upload.dropActive') : t('script.upload.drop')}
            </p>
            <Input
              ref={input}
              type="file"
              accept="application/pdf,.pdf"
              aria-label={t('script.upload.chooseLabel')}
              className="sr-only"
              tabIndex={-1}
              onChange={(event) => choose(event.target.files?.[0])}
            />
            <Button variant="primary" onClick={() => input.current?.click()}>
              <UploadIcon aria-hidden="true" />
              {t('script.upload.choose')}
            </Button>
          </div>
        )}
        {state.phase === 'refused' ? (
          <div role="alert" className="grid gap-1 text-sm">
            <p className="text-destructive">{t(REFUSAL_KEYS[state.reason])}</p>
            <p className="text-muted-foreground">{t('script.upload.another')}</p>
          </div>
        ) : null}
        {state.phase === 'failed' ? (
          <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
            <p className="text-destructive">
              {t('script.upload.failed')} {state.message}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void retry()}>
              {t('script.upload.retry')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
